import { Box, Stack, Typography } from "@mui/material";
import { chain } from "lodash-es";
import { useMemo } from "react";

import {
  PermissionFunctionalGroup,
  PermissionGroup,
  PermissionMetaDto,
} from "@/core/api/generated/v1";

import FoldableBlock from "../Display/FoldableBlock";
import InlineApiEnumValue from "../Enum/InlineApiEnumValue";
import PermissionDisplay from "./PermissionDisplay";

interface Props {
  permissions: PermissionMetaDto[] | undefined;
}

export default function PermissionList({ permissions }: Props) {
  const permissionsGroupedByFuncGroupAndGroup = useMemo(
    () =>
      chain(permissions)
        .orderBy((x) => x.funcGroupMeta?.sortOrder ?? 0, "asc")
        .groupBy((x) => x.funcGroupMeta?.value || null)
        .mapValues((group) =>
          chain(group)
            .orderBy((x) => x.groupMeta?.sortOrder ?? 0, "asc")
            .groupBy((x) => x.groupMeta?.value || null)
            .mapValues((group2) =>
              chain(group2)
                .orderBy((x) => x.sortOrder ?? 0, "asc")
                .value(),
            )
            .value(),
        )
        .value(),
    [permissions],
  );

  return (
    <Stack spacing={1}>
      {/* Plain list */}
      {/* {permissions?.map((permission, i) => (
        <PermissionDisplay key={i} variant='block' permission={permission} withIcon />
      ))} */}

      <Stack spacing={0.5}>
        {Object.keys(permissionsGroupedByFuncGroupAndGroup).map((groupKey1, i) => {
          const permFunctionalGroup = groupKey1 as PermissionFunctionalGroup;
          const groupedByPermGroup = permissionsGroupedByFuncGroupAndGroup[groupKey1];

          return (
            <Box key={`${permFunctionalGroup}-${i}`}>
              <FoldableBlock
                defaultIsFolded={false}
                trigger={{
                  size: "small",
                  label: (
                    <Typography component='div' variant='subtitle1'>
                      <InlineApiEnumValue
                        type='PermissionFunctionalGroup'
                        value={permFunctionalGroup}
                        withHelperTooltip
                      />
                    </Typography>
                  ),
                }}
              >
                <Stack spacing={1} sx={{ justifyContent: "flex-start", alignItems: "flex-start" }}>
                  <Stack spacing={1} sx={{ pl: 2 }}>
                    {Object.keys(groupedByPermGroup).map((groupKey2, j) => {
                      const permGroup = groupKey2 as PermissionGroup;
                      const perms = groupedByPermGroup[groupKey2];

                      return (
                        <Stack key={`${permGroup}-${j}`} spacing={0.5}>
                          <Typography component='div' variant='subtitle2'>
                            <InlineApiEnumValue
                              type='PermissionGroup'
                              value={permGroup}
                              withHelperTooltip
                            />
                          </Typography>

                          <Stack spacing={0} sx={{ pl: 2 }}>
                            {perms.map((perm, z) => (
                              <PermissionDisplay
                                key={`${perm.value}-${z}`}
                                variant='block'
                                permission={perm}
                                withIcon
                              />
                            ))}
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              </FoldableBlock>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
